import { Aide } from '@/components/ui/aide';
import ShortagePanel from '../components/ShortagePanel';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { tripsAPI, trucksAPI, driversAPI, geoLocationsAPI, operationsAPI, tripIntegrityAPI, trackingAPI } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { lotsAPI } from '../lib/lotsApi';
import { arrondiDevise, formatDateHeure } from '../lib/formats';
import { libelle, nomPays, texteSur } from '../lib/libelles';
import { groupTransportChecks } from '../lib/transportReadiness';
import { volumeCharge } from '../lib/volumeCharge';
import { Marker, Polyline, Popup } from 'react-leaflet';
import MapSurface from '../components/map/MapSurface';
import RouteLayer from '../components/map/RouteLayer';
import ZoneLayer from '../components/map/ZoneLayer';
import ZonesFiltre from '../components/map/ZonesFiltre';
import SelecteurFond from '../components/map/SelecteurFond';
import MoletteSurFocus from '../components/map/MoletteSurFocus';
import { MOUVEMENT_VITESSE_MIN_KMH } from '../components/map/fleetRules';
import CoucheRejeu from '../components/map/CoucheRejeu';
import LecteurTrajet from '../components/map/LecteurTrajet';
import { truckIcon } from '../components/map/icons';
import LienSuiviDialog from '../components/trip/LienSuiviDialog';
import { statusColor } from '../components/map/status';
import { enregistrerFondPrefere, lireFondPrefere } from '../components/map/fondPrefere';
import { filtrerZones } from '../components/map/famillesZones';
import {
  classifyCorridorZones,
  formatDistance,
  parseRouteGeometry,
  portionsHorsItineraire,
  routeLengthMeters,
  splitRouteAtPosition,
} from '../components/map/geo';
import useFleetStream from '../hooks/useFleetStream';
import {
  Clock,
  MapPin,
  MoreHorizontal,
  User,
  Briefcase,
  Truck,
  Fuel,
  Loader2,
  Check,
  AlertTriangle,
  Edit,
  Trash2,
  ArrowRight,
  Link2,
  Paperclip,
  Scale,
  Package,
  Navigation,
  Play,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../components/ui/sonner';
import {
  cn,
  getStatusBadgeClass,
  formatDateTime,
  formatCurrency,
  formatVolume,
  getNextStatuses,
  isCustomsStatus,
} from '../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { EmptyState } from '../components/ui/empty-state';
import TripTimeline from '../components/animations/TripTimeline';
import WhatsAppShareButton from '../components/ui/WhatsAppShareButton';
import { VoiceInputButton } from '../components/ui/VoiceInputButton';
import SwipeToConfirm from '../components/ui/SwipeToConfirm';
import { useAuth } from '../context/AuthContext';
import { useSyncOuEnLigne } from '../context/SyncContext';
import BandeauAReparer from '../components/business/BandeauAReparer';
import { pointGpsRetenu, pointsGpsRetenus } from '../lib/pointsGps';
import { detecterArrets, segmentsParVitesse } from '../lib/arretsTrajet';
import {
  fusionnerTrace,
  pointLePlusRecent,
  positionsTrace,
  releveRecent,
  dernierPointTrace,
} from '../lib/traceGps';
import LigneArgent from '../components/business/LigneArgent';
import ChampsAvances from '../components/ui/champs-avances';
import usePrixPropose, { appliquerPrixPropose } from '../hooks/usePrixPropose';
import { PageHeader } from '../components/ui/page-header';
import SignalerPositionDialog from '../components/trip/SignalerPositionDialog';
import { LoadingState } from '../components/ui/loading-state';
import { StatsCompactes } from '../components/ui/stats-compactes';
import ExpenseForm from '../components/finance/ExpenseForm';
import ExpensesTable from '../components/finance/ExpensesTable';
import ObservationsPanel from '../components/observations/ObservationsPanel';
import TripFreightPanel from '../components/trip/TripFreightPanel';
import TripLoadingInfoCard from '../components/trip/TripLoadingInfoCard';
import TripStationChain from '../components/stations/supply/TripStationChain';
import TeleverseurEnLigne from '../components/business/TeleverseurEnLigne';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

// Les statuts pendant lesquels une position bouge vraiment: hors de cette
// liste, ouvrir un socket ne remonterait jamais rien.
const ACTIVE_TRACKING_STATUSES = [
  'EN_ROUTE_TO_BORDER',
  'LOADING',
  'EN_ROUTE_TO_LOADING',
  'EN_ROUTE_TO_ENTRY_CUSTOMS',
  'EN_ROUTE_TO_UNLOADING',
  'UNLOADING',
  'RETURN_JOURNEY',
];

// Les quatre onglets de la page, dans l'ordre où un opérateur en a besoin.
// L'onglet « Argent » n'existe que pour ceux qui voient les montants; les
// autres rôles gardent la saisie des frais sous le nom « Frais du trajet ».
export const TRIP_TABS = [
  { value: 'chargement', label: 'Chargement et livraison' },
  { value: 'argent', label: 'Argent', labelSansMontants: 'Frais du trajet' },
  { value: 'pieces', label: 'Pièces' },
  { value: 'historique', label: 'Historique' },
];

const DEFAULT_TRIP_TAB = 'chargement';

/** Relève de la position quand le flux direct du trajet est coupé. */
const RELEVE_SANS_FLUX_MS = 30000;

const TripDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user, canSeeMoney, managesDepot, hasModule } = useAuth();
  // Valeur simple (pas la fonction, recréée à chaque rendu) : elle peut figurer
  // dans les dépendances du chargement sans le relancer en boucle.
  const peutLireLots = typeof hasModule === 'function' && hasModule('lots');

  const [trip, setTrip] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  // Ce que la course a coûté et rapporté. La recette vient des allocations:
  // elle n'était pas calculable tant qu'un camion ne portait qu'une commande
  // et que son volume n'était ventilé nulle part.
  const [economics, setEconomics] = useState(null);
  // Étapes proposées par une zone, le chauffeur ou le bureau: elles attendent
  // une confirmation en un geste (lot 051).
  const [propositions, setPropositions] = useState([]);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);
  const [showPosition, setShowPosition] = useState(false);
  const [isRecomputingIntegrity, setIsRecomputingIntegrity] = useState(false);
  const [truck, setTruck] = useState(null);
  const [driver, setDriver] = useState(null);
  const [operation, setOperation] = useState(null);
  // Le lot attribué du trajet (lot 057), quand il en fait partie.
  const [lot, setLot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Combien de points « à corriger » le bandeau intégré à « À faire » compte.
  const [nbManques, setNbManques] = useState(0);
  
  // GPS tracking state
  const [gpsData, setGpsData] = useState(null);
  const [gpsHistory, setGpsHistory] = useState([]);
  const [trackingConnected, setTrackingConnected] = useState(false);
  const [showMap, setShowMap] = useState(false);
  // Lecture du trajet complet (« Rejouer »): chargée une fois, à la demande.
  const [rejeuOuvert, setRejeuOuvert] = useState(false);
  const [rejeuPoints, setRejeuPoints] = useState(null);
  const [rejeuChargement, setRejeuChargement] = useState(false);
  const [rejeuErreur, setRejeuErreur] = useState(false);
  const [rejeuIndex, setRejeuIndex] = useState(0);
  // Fond de carte (le même choix que sur le suivi de la flotte) et familles de
  // zones décochées dans la fenêtre « Zones ».
  const [fondCarte, setFondCarte] = useState(lireFondPrefere);
  const [famillesMasquees, setFamillesMasquees] = useState([]);
  const [corridorZones, setCorridorZones] = useState([]);

  // Status update modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newCustomsCheckpoint, setNewCustomsCheckpoint] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Une seule modale pour les volumes et les montants. Il y en avait deux —
  // « détails du trajet » et « données financières » — qui écrivaient toutes
  // les deux la consommation réelle et le carburant manquant: l'exploitant
  // saisissait le même litrage à deux endroits, et la dernière enregistrée
  // écrasait l'autre sans que rien ne dise laquelle.
  const [showVolumesModal, setShowVolumesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLienSuivi, setShowLienSuivi] = useState(false);

  // Les frais du trajet vivent dans le journal (`ExpensesTable`): ce compteur
  // la fait se recharger après une saisie ou une annulation.
  const [expensesVersion, setExpensesVersion] = useState(0);

  const [saisieVolumes, setSaisieVolumes] = useState({
    fuel_quantity_real: '',
    fuel_missing_quantity: '',
    fuel_price_unit: '',
    return_date: '',
    fuel_quantity_estimated: '',
    conso_espe: '',
    road_expenses: '',
  });

  // Le barème propose, il n'impose pas: dès que l'exploitant tape un prix
  // négocié, plus rien ne le réécrit.
  const [prixTouche, setPrixTouche] = useState(false);

  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    try {
      if (!silencieux) setIsLoading(true);
      const [tripData, updatesData, readinessData, integrityData, economicsData, propositionsData] =
        await Promise.all([
          tripsAPI.getById(id),
          tripsAPI.getUpdates(id),
          tripsAPI.getReadiness(id).catch(() => null),
          tripIntegrityAPI.getScore(id).catch(() => null),
          tripsAPI.getEconomics(id).catch(() => null),
          Promise.resolve()
            .then(() => tripsAPI.getProposals(id))
            .catch(() => []),
        ]);

      setTrip(tripData);
      setUpdates(updatesData);
      setReadiness(readinessData);
      setIntegrity(integrityData);
      setEconomics(economicsData);
      setPropositions(Array.isArray(propositionsData) ? propositionsData : []);

      setSaisieVolumes({
        fuel_quantity_real: tripData.fuel_quantity_real || '',
        fuel_missing_quantity: tripData.fuel_missing_quantity || '',
        fuel_price_unit: tripData.fuel_price_unit || '',
        return_date: tripData.return_date
          ? String(tripData.return_date).slice(0, 10)
          : '',
        fuel_quantity_estimated: tripData.fuel_quantity_estimated ?? '',
        conso_espe: tripData.conso_espe || '',
        road_expenses: tripData.road_expenses || '',
      });

      // Load related data
      // Un trajet de lot n'a pas forcément de convoi (lot 057), et la
      // référence du lot se lit seulement avec le module des lots.
      const lireLot =
        tripData.lot_id && peutLireLots
          ? lotsAPI
              .get(tripData.lot_id, { skipToast: true })
              .then((detailLot) => detailLot?.lot || null)
              .catch(() => null)
          : Promise.resolve(null);
      const [truckData, operationData, lotData] = await Promise.all([
        trucksAPI.getById(tripData.truck_id),
        tripData.operation_id ? operationsAPI.getById(tripData.operation_id) : Promise.resolve(null),
        lireLot,
      ]);

      setTruck(truckData);
      setOperation(operationData);
      setLot(lotData);

      if (tripData.driver_id) {
        const driverData = await driversAPI.getById(tripData.driver_id);
        setDriver(driverData);
      }
    } catch (err) {
      console.error('Failed to load trip:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id, peutLireLots]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['trajets', 'stock', 'depenses', 'finance', 'documents', 'commandes'], loadData);


  // Le prix au litre que la société a déjà déclaré pour ce genre de convoi.
  // Il n'était demandé qu'à la main, sur un écran qui suggérait « Ex: 725 » —
  // un exemple que plus d'un exploitant a fini par saisir tel quel. On ne
  // cherche qu'une fois la modale ouverte: sur un trajet qu'on ne fait que
  // lire, c'est une requête de moins.
  const {
    prix: prixBareme,
    nomBareme,
    chargement: chargementBareme,
    aucunBareme,
  } = usePrixPropose({
    operationType: operation?.type,
    actif: showVolumesModal,
  });

  useEffect(() => {
    if (!showVolumesModal) return;
    setSaisieVolumes((prev) => ({
      ...prev,
      fuel_price_unit: appliquerPrixPropose({
        prix: prixBareme,
        valeurActuelle: prev.fuel_price_unit,
        toucheParLHumain: prixTouche,
      }),
    }));
  }, [showVolumesModal, prixBareme, prixTouche]);

  // Le recalcul ne repasse pas par `loadData`: celui-ci remet la page en
  // chargement et démonterait l'onglet sous les doigts de l'opérateur.
  const recomputeIntegrity = useCallback(async () => {
    setIsRecomputingIntegrity(true);
    try {
      const score = await tripIntegrityAPI.recompute(id);
      setIntegrity(score);
      toast.success('Contrôle de la livraison refait');
    } catch (err) {
      toast.error('Le contrôle de la livraison n’a pas pu être refait.');
    } finally {
      setIsRecomputingIntegrity(false);
    }
  }, [id]);

  // Le suivi temps réel passe par le hook partagé: même reconnexion à backoff
  // exponentiel, même keepalive, même construction d'URL que les écrans flotte.
  const handleGpsUpdate = useCallback((point) => {
    // Un relevé écarté par le serveur ne déplace pas le camion.
    if (!pointGpsRetenu(point)) return;
    // Le tracé reste dans l'ordre du temps, quel que soit l'ordre d'arrivée.
    setGpsData((prev) => pointLePlusRecent(prev, point));
    setGpsHistory((prev) => fusionnerTrace(prev, [point]));
  }, []);

  const trackingActive =
    Boolean(showMap && trip) && ACTIVE_TRACKING_STATUSES.includes(trip?.status);

  const { connected: streamConnected } = useFleetStream({
    mode: 'trip',
    tripId: trip?.id,
    token,
    enabled: trackingActive,
    onGpsUpdate: handleGpsUpdate,
  });

  useEffect(() => {
    setTrackingConnected(streamConnected);
  }, [streamConnected]);

  // Flux direct coupé (réseau, proxy) : la position est relue toutes les 30 s
  // jusqu'à ce qu'il revienne, au lieu de rester figée.
  useEffect(() => {
    if (!trackingActive || streamConnected || !trip?.id) return undefined;
    const relever = async () => {
      try {
        const rows = pointsGpsRetenus(await trackingAPI.getHistory(trip.id, { limit: 50 }));
        if (rows.length === 0) return;
        setGpsHistory((prev) => fusionnerTrace(prev, rows));
        const dernier = dernierPointTrace(fusionnerTrace([], rows));
        if (dernier) setGpsData((prev) => pointLePlusRecent(prev, dernier));
      } catch {
        // Nouvel essai au prochain tour.
      }
    };
    const minuterie = window.setInterval(relever, RELEVE_SANS_FLUX_MS);
    return () => window.clearInterval(minuterie);
  }, [trackingActive, streamConnected, trip?.id]);

  // Fetch GPS history when map is shown
  useEffect(() => {
    if (!showMap || !trip) return;

    const fetchGpsHistory = async () => {
      try {
        // Passe par l'instance axios partagée (lib/api) : bearer injecté,
        // base URL unique — plus de fetch brut avec URL reconstruite à la main.
        // Le serveur rend du plus récent au plus ancien et plafonne à 1000
        // relevés: 500 couvrent largement les dernières heures de route.
        const history = await trackingAPI.getHistory(trip.id, { limit: 500 });
        // Les relevés écartés (saut impossible, qualité nulle) restent dans
        // l'historique du serveur: ni position actuelle, ni tracé.
        // Fusionné (et non remplacé) avec les points déjà reçus en direct, et
        // rangé du plus ancien au plus récent.
        const rows = pointsGpsRetenus(history);
        setGpsHistory((prev) => fusionnerTrace(prev, rows));
        const dernier = dernierPointTrace(fusionnerTrace([], rows));
        if (dernier) setGpsData((prev) => pointLePlusRecent(prev, dernier)); // Dernière position retenue
      } catch (error) {
        console.error('Error fetching GPS history:', error);
      }
    };

    fetchGpsHistory();
  }, [showMap, trip, token]);

  // Le référentiel de zones n'est chargé qu'une fois la carte ouverte: sur un
  // trajet qu'on ne fait que lire, c'est une requête de moins.
  useEffect(() => {
    if (!showMap) return;
    let cancelled = false;
    geoLocationsAPI
      .getAll({ status: 'active', limit: 1000 })
      .then((data) => {
        if (cancelled) return;
        setCorridorZones(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showMap]);

  /**
   * `route_geometry_json` et `remaining_distance_meters` sont produits par un
   * autre service et peuvent tout simplement ne pas être là. Absents, la carte
   * retombe sur la trace GPS seule : rien ne casse, rien n'est inventé.
   */
  const routePoints = useMemo(
    () => parseRouteGeometry(trip?.route_geometry_json),
    [trip?.route_geometry_json],
  );

  const currentPosition = useMemo(
    () =>
      gpsData?.latitude != null && gpsData?.longitude != null
        ? [gpsData.latitude, gpsData.longitude]
        : null,
    [gpsData],
  );

  const remainingLabel = useMemo(() => {
    const declared = formatDistance(trip?.remaining_distance_meters);
    if (declared) return declared;
    // Sans distance annoncée, la longueur du tronçon restant du tracé est une
    // approximation honnête — elle suit la route, pas la ligne droite.
    if (routePoints.length < 2) return null;
    const { remaining } = splitRouteAtPosition(routePoints, currentPosition);
    return formatDistance(routeLengthMeters(remaining));
  }, [trip?.remaining_distance_meters, routePoints, currentPosition]);

  const mapZones = useMemo(
    () =>
      routePoints.length > 1
        ? classifyCorridorZones(corridorZones, routePoints, currentPosition)
        : corridorZones,
    [corridorZones, routePoints, currentPosition],
  );

  // gpsHistory est déjà chronologique (voir lib/traceGps): pas de retournement.
  const trailPositions = useMemo(() => positionsTrace(gpsHistory), [gpsHistory]);
  const horsItineraire = useMemo(
    () => portionsHorsItineraire(routePoints, trailPositions),
    [routePoints, trailPositions],
  );

  const peutRejouer = trailPositions.length >= 2;
  const rejeuActif = rejeuOuvert && Array.isArray(rejeuPoints) && rejeuPoints.length >= 2;

  const rejeuAnalyse = useMemo(
    () =>
      rejeuPoints
        ? { segments: segmentsParVitesse(rejeuPoints), arrets: detecterArrets(rejeuPoints) }
        : null,
    [rejeuPoints],
  );

  const ouvrirRejeu = useCallback(async () => {
    setRejeuErreur(false);
    if (rejeuPoints) {
      setRejeuIndex(0);
      setRejeuOuvert(true);
      return;
    }
    setRejeuChargement(true);
    try {
      const rows = await trackingAPI.getHistory(trip.id, { order: 'asc', max_points: 3000 });
      const trace = fusionnerTrace([], pointsGpsRetenus(rows), 5000);
      if (trace.length < 2) throw new Error('trace trop courte');
      setRejeuPoints(trace);
      setRejeuIndex(0);
      setRejeuOuvert(true);
    } catch (error) {
      console.error('Error fetching full trip trace:', error);
      setRejeuErreur(true);
    } finally {
      setRejeuChargement(false);
    }
  }, [rejeuPoints, trip?.id]);

  const fermerRejeu = useCallback(() => setRejeuOuvert(false), []);

  // Carte masquée: la lecture se referme, le prochain « Rejouer » repart du début.
  useEffect(() => {
    if (!showMap) {
      setRejeuOuvert(false);
      setRejeuErreur(false);
    }
  }, [showMap]);

  const pointRejeu = rejeuActif ? rejeuPoints[Math.min(rejeuIndex, rejeuPoints.length - 1)] : null;

  const iconeCamionRejeu = useMemo(
    () =>
      pointRejeu
        ? truckIcon({
            color: statusColor(trip?.status),
            matricule: truck?.matricule,
            online: true,
            heading: pointRejeu.heading ?? null,
            moving: Number(pointRejeu.speed_kmh) >= MOUVEMENT_VITESSE_MIN_KMH,
          })
        : null,
    [pointRejeu, trip?.status, truck?.matricule],
  );

  const iconeCamion = useMemo(
    () =>
      truckIcon({
        color: statusColor(trip?.status),
        matricule: truck?.matricule,
        online: trackingConnected || releveRecent(gpsData),
      }),
    [trip?.status, truck?.matricule, trackingConnected, gpsData],
  );

  const zonesVisibles = useMemo(
    () => filtrerZones(mapZones, famillesMasquees),
    [mapZones, famillesMasquees],
  );

  const choisirFond = useCallback((fond) => {
    setFondCarte(fond);
    enregistrerFondPrefere(fond);
  }, []);

  const handleStatusUpdate = async () => {
    if (!newStatus) return;

    setIsSubmitting(true);
    try {
      await tripsAPI.updateStatus(id, {
        trip_id: id,
        status: newStatus,
        location_text: newLocation || null,
        customs_checkpoint: isCustomsStatus(newStatus)
          ? newCustomsCheckpoint
          : null,
        note: newNote || null,
      });

      setShowStatusModal(false);
      setNewStatus('');
      setNewLocation('');
      setNewCustomsCheckpoint('');
      setNewNote('');

      toast.success('Statut mis à jour');

      await loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error(messageErreur(err, 'Le statut du trajet n’a pas pu être mis à jour.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmerProposition = async (proposition) => {
    if (confirmationEnCours || !proposition?.id) return;
    setConfirmationEnCours(true);
    try {
      await tripsAPI.confirmProposal(id, proposition.id);
      toast.success('Étape confirmée');
      await loadData({ silencieux: true });
    } catch (err) {
      toast.error(messageErreur(err, 'L’étape n’a pas pu être confirmée.'));
      await loadData({ silencieux: true });
    } finally {
      setConfirmationEnCours(false);
    }
  };

  const selectTab = (value) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('onglet', value);
        return next;
      },
      { replace: true, state: location.state },
    );
  };

  // « Ajouter une pièce » (contrôles métier): ouvre l'onglet Pièces, où se
  // trouve le dépôt des justificatifs du trajet, et y amène le regard.
  const ouvrirJustificatifs = () => {
    selectTab('pieces');
    window.requestAnimationFrame(() => {
      document.getElementById('justificatifs-du-trajet')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleNewStatusChange = (value) => {
    setNewStatus(value);
    if (!isCustomsStatus(value)) {
      setNewCustomsCheckpoint('');
    }
  };

  // Après un frais saisi: le tableau des frais se recharge, et le trajet aussi
  // (ses frais de route et sa marge en dépendent).
  // Rechargement discret : `loadData` remplace la page par un écran d'attente
  // et ferait perdre au formulaire son message de confirmation.
  const refreshTripMoney = async () => {
    try {
      const [tripData, economicsData] = await Promise.all([
        tripsAPI.getById(id),
        tripsAPI.getEconomics(id).catch(() => null),
      ]);
      setTrip(tripData);
      setEconomics(economicsData);
    } catch (error) {
      // Les chiffres se rafraîchiront à la prochaine ouverture de la page.
    }
  };

  const handleExpenseSaved = async () => {
    setExpensesVersion((v) => v + 1);
    await refreshTripMoney();
  };

  // Un frais n'est jamais effacé: il est annulé par une écriture contraire.
  const handleCancelExpense = async (expense) => {
    if (!window.confirm('Annuler ce frais ? Il restera visible dans le journal, annulé.')) return;
    setIsSubmitting(true);
    try {
      await tripsAPI.deleteExpense(id, expense.id);
      setExpensesVersion((v) => v + 1);
      await refreshTripMoney();
      toast.success('Frais annulé.');
    } catch (error) {
      toast.error(messageErreur(error, 'Le frais n’a pas pu être annulé.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Le montant du trajet: litres chargés × prix au litre, plus les espèces et
  // les frais de route. Il n'est plus saisissable à côté de son propre calcul.
  const calculateTotal = () => {
    const qty = parseFloat(saisieVolumes.fuel_quantity_real) || 0;
    const price = parseFloat(saisieVolumes.fuel_price_unit) || 0;
    const espe = parseFloat(saisieVolumes.conso_espe) || 0;
    const road = parseFloat(saisieVolumes.road_expenses) || 0;
    // Arrondi au franc comme tout montant posé par le serveur: 2,3 L × 675
    // s'enregistrait 1 552,5 (et s'affichait 1 552 ou 1 553 selon l'écran).
    return arrondiDevise(qty * price + espe + road);
  };

  const enregistrerVolumesEtMontants = async () => {
    setIsSubmitting(true);
    try {
      const total = calculateTotal();
      await tripsAPI.update(id, {
        fuel_quantity_real: saisieVolumes.fuel_quantity_real
          ? parseFloat(saisieVolumes.fuel_quantity_real)
          : null,
        fuel_missing_quantity: saisieVolumes.fuel_missing_quantity
          ? parseFloat(saisieVolumes.fuel_missing_quantity)
          : null,
        fuel_price_unit: saisieVolumes.fuel_price_unit
          ? parseFloat(saisieVolumes.fuel_price_unit)
          : null,
        return_date: saisieVolumes.return_date || null,
        fuel_quantity_estimated: saisieVolumes.fuel_quantity_estimated
          ? parseFloat(saisieVolumes.fuel_quantity_estimated)
          : null,
        conso_espe: saisieVolumes.conso_espe
          ? parseFloat(saisieVolumes.conso_espe)
          : null,
        road_expenses: saisieVolumes.road_expenses
          ? parseFloat(saisieVolumes.road_expenses)
          : null,
        // Le montant enregistré est celui que la modale affiche. Il existait
        // un champ « si différent » juste en dessous du calcul: il ne servait
        // qu'à créer un écart que personne ne savait ensuite expliquer.
        total_amount: total > 0 ? total : null,
      });

      setShowVolumesModal(false);
      await loadData();

      toast.success('Volumes et montants enregistrés');
    } catch (err) {
      console.error('Failed to update trip volumes and amounts:', err);
      toast.error(messageErreur(err, 'Les volumes et montants n’ont pas pu être enregistrés.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTrip = async () => {
    setIsSubmitting(true);
    try {
      await tripsAPI.delete(id);
      setShowDeleteModal(false);
      toast.success('Trajet supprimé');
      navigate('/trips');
    } catch (err) {
      console.error('Failed to delete trip:', err);
      toast.error(messageErreur(err, 'Le trajet n’a pas pu être supprimé.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted-foreground">Trajet non trouvé</p>
        <Button className="mt-4" onClick={() => navigate('/trips')}>
          Retour aux trajets
        </Button>
      </div>
    );
  }

  const nextStatuses = getNextStatuses(trip.status, { managesDepot });
  const isComplete = ['COMPLETED', 'CANCELLED'].includes(trip.status);
  const requestedTab = searchParams.get('onglet');
  const activeTab = TRIP_TABS.some((tab) => tab.value === requestedTab)
    ? requestedTab
    : DEFAULT_TRIP_TAB;
  // `getNextStatuses` met l'annulation en tête de la liste du menu: la
  // prochaine étape est la première étape qui fait avancer le trajet.
  const prochainStatut = nextStatuses.find((statut) => statut !== 'CANCELLED') || null;
  const ouvrirAvancement = (statut) => {
    handleNewStatusChange(statut);
    setShowStatusModal(true);
  };

  // Numbers, not formatted strings: Readout draws a gauge only when it gets a
  // number and a capacity, and the litres are exactly the kind of value that
  // means nothing without the tank it sits in.
  const loadedLitres =
    Number(trip.fuel_quantity_real) || Number(trip.fuel_quantity_estimated) || 0;
  const missingLitres = Number(trip.fuel_missing_quantity) || 0;
  const truckCapacity = Number(truck?.capacity_liters) || 0;

  const margeVisible =
    canSeeMoney &&
    economics &&
    economics.margin != null &&
    (economics.revenue > 0 || economics.total_cost > 0);

  const checklist = readiness?.checklist || [];
  const { watched: controlesAtraiter, ready: controlesPrets } = groupTransportChecks(checklist);
  const nbAFaire = controlesAtraiter.length + nbManques;
  // Rien à afficher tant que ni les contrôles ni le bandeau n'ont de réponse.
  const zoneAFaireVisible = nbAFaire > 0 || checklist.length > 0;
  const volume = volumeCharge(trip);

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="trip-detail-page">
      {/* En-tête compact: l'itinéraire est le titre, trois chiffres au plus,
          une seule action principale (l'étape suivante). Le statut vit dans
          la frise, juste dessous: il n'est dit qu'une fois. */}
      <PageHeader
        title={
          // Un trajet de lot attribué n'a ni pays de départ ni pays d'arrivée : la plaque tient lieu de titre.
          !trip.departure_country && !trip.destination_country && (truck?.matricule || trip.tractor_plate)
            ? truck?.matricule || trip.tractor_plate
            : `${nomPays(trip.departure_country)} → ${nomPays(trip.destination_country)}`
        }
        backTo="/trips"
        backLabel="Retour aux trajets"
        actionsDisposition="grille"
        statsDisposition="pile"
        stats={[
          {
            label: 'Volume chargé',
            valeur: loadedLitres,
            unite: 'L',
            aide: truckCapacity
              ? `Citerne ${formatVolume(truckCapacity)}`
              : `Estimé ${formatVolume(trip.fuel_quantity_estimated)}`,
          },
          {
            label: 'Manquant',
            valeur: missingLitres,
            unite: 'L',
            ton: missingLitres > 0 ? 'attention' : 'positif',
            aide: missingLitres > 0 ? 'Écart à justifier' : 'Aucun écart',
          },
          margeVisible
            ? {
                label: 'Marge',
                valeur: formatCurrency(economics.margin),
                ton: Number(economics.margin) < 0 ? 'critique' : 'neutre',
                aide: 'Ce que le trajet rapporte, moins ce qu’il coûte',
              }
            : null,
        ].filter(Boolean)}
        alertesSousTitre
        alertes={[
          ...propositions.slice(0, 1).map((proposition) => ({
            niveau: 'attention',
            court: 'Étape à confirmer',
            texte: `${libelle('tripStatus', proposition.target_status)} : ${
              proposition.origin === 'driver'
                ? 'signalé par le chauffeur'
                : proposition.origin === 'office'
                  ? 'signalé par le bureau'
                  : 'détecté par la position du camion'
            }${proposition.place ? ` (${proposition.place})` : ''}.`,
          })),
          economics?.missing_fields?.length > 0
            ? {
                niveau: 'attention',
                court: 'Montants à compléter',
                texte:
                  'Montants à compléter : prix de vente ou coût manquant. La marge ne peut pas encore être établie.',
                action: { label: 'Modifier les volumes et montants', onClick: () => setShowVolumesModal(true) },
              }
            : null,
        ]}
        actions={
          <>
            {!isComplete && propositions[0] && (
              <Button
                className="col-span-2"
                onClick={() => confirmerProposition(propositions[0])}
                disabled={confirmationEnCours}
                data-testid="confirm-proposal-btn"
              >
                Confirmer : {libelle('tripStatus', propositions[0].target_status)}
              </Button>
            )}
            {!isComplete && prochainStatut && propositions[0]?.target_status !== prochainStatut && (
              <Button
                className="col-span-2"
                onClick={() => ouvrirAvancement(prochainStatut)}
                data-testid="next-step-btn"
              >
                {libelle('tripStatus', prochainStatut)}
              </Button>
            )}
            <WhatsAppShareButton trip={trip} truck={truck} driver={driver} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="!col-span-1" data-testid="trip-more-actions">
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  Autres actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isComplete && (
                  <DropdownMenuItem
                    onSelect={() => setShowPosition(true)}
                    data-testid="report-position-btn"
                  >
                    Signaler la position
                  </DropdownMenuItem>
                )}
                {!isComplete && (
                  <DropdownMenuItem
                    onSelect={() => setShowStatusModal(true)}
                    data-testid="update-status-btn"
                  >
                    Changer le statut
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setShowLienSuivi(true)} data-testid="lien-suivi-btn">
                  <Link2 className="h-4 w-4" aria-hidden="true" />
                  Lien de suivi
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setShowDeleteModal(true)}
                  disabled={isSubmitting}
                  data-testid="delete-trip-btn"
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <SignalerPositionDialog
        tripId={id}
        open={showPosition}
        onOpenChange={setShowPosition}
        onDone={() => loadData({ silencieux: true })}
      />

      {/* Où en est ce trajet: la section « Avancement du trajet », puis de quoi
          il s'agit — une seule ligne d'identité (convoi, camion, chauffeur, départ). */}
      <div data-testid="avancement-trajet">
        <TripTimeline currentStatus={trip?.status} statusHistory={updates} managesDepot={managesDepot} />
      </div>
      <section className="rounded-xl border border-border bg-card px-4 py-3" data-testid="situation-trajet">
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm"
          data-testid="identite-trajet"
        >
          {operation || !trip?.lot_id ? (
            <IdentityFact icon={Briefcase} label="Convoi" vide={!operation} texteVide="Hors convoi">
              {operation ? (
                <>
                  <span className="font-mono font-semibold text-foreground">{operation.code}</span>
                  {operation.beneficiary_name || operation.type ? (
                    <span className="min-w-0 truncate text-muted-foreground">
                      {' · '}
                      {operation.beneficiary_name || libelle('operationType', operation.type)}
                    </span>
                  ) : null}
                </>
              ) : null}
            </IdentityFact>
          ) : null}
          {trip?.lot_id ? (
            <IdentityFact icon={Briefcase} label="Lot" vide={!lot} texteVide="Lot attribué">
              {lot ? (
                <>
                  <Link
                    to={`/lots/${lot.id}`}
                    className="font-mono font-semibold text-primary underline-offset-2 hover:underline"
                    data-testid="trip-lot-link"
                  >
                    {lot.reference}
                  </Link>
                  {lot.transporter_name ? (
                    <span className="min-w-0 truncate text-muted-foreground">
                      {' · '}
                      {lot.transporter_name}
                    </span>
                  ) : null}
                </>
              ) : null}
            </IdentityFact>
          ) : null}
          <IdentityFact icon={Truck} label="Camion" vide={!truck} texteVide="Camion non affecté">
            {truck ? (
              <>
                <span className="font-semibold text-foreground">{truck.matricule}</span>
                {truck.capacity_liters ? (
                  <span className="text-muted-foreground">{' · '}{formatVolume(truck.capacity_liters)}</span>
                ) : null}
              </>
            ) : null}
          </IdentityFact>
          {/* Sans chauffeur, la ligne n'en parle pas: la zone « À faire »
              propose déjà de l'affecter. */}
          {driver?.full_name ? (
            <IdentityFact icon={User} label="Chauffeur">
              <span className="min-w-0 truncate font-semibold text-foreground">{driver.full_name}</span>
              {driver.phone ? (
                <a
                  href={`tel:${driver.phone}`}
                  className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  {' · '}
                  {driver.phone}
                </a>
              ) : null}
            </IdentityFact>
          ) : null}
          <IdentityFact icon={Clock} label="Départ">
            <span className="font-semibold text-foreground">{formatDateTime(trip.departure_date)}</span>
            {trip.return_date ? (
              <span className="text-muted-foreground">{' · retour '}{formatDateTime(trip.return_date)}</span>
            ) : null}
          </IdentityFact>
        </div>
      </section>

      {/* Une seule zone « À faire », juste sous la frise: ce qui manque sur le
          trajet (chauffeur, contrôle de départ, refus du serveur) puis les
          contrôles à traiter, chacun avec son bouton. Les contrôles réussis se
          réduisent au compteur. */}
      <section
        hidden={!zoneAFaireVisible}
        className="overflow-hidden rounded-xl border border-border bg-card"
        data-testid="a-faire"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">À faire</h2>
            {nbAFaire > 0 ? (
              <Badge variant="warning" className="font-mono" data-testid="a-faire-compteur">
                {nbAFaire}
              </Badge>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-sm text-muted-foreground"
                data-testid="a-faire-ok"
              >
                <Check className="h-4 w-4 text-success" aria-hidden="true" />
                Tout est en ordre
              </span>
            )}
          </div>
          {checklist.length > 0 ? (
            <Badge
              variant={controlesPrets.length === checklist.length ? 'success' : 'muted'}
              className="shrink-0 font-mono"
              title="Contrôles du trajet réussis"
              data-testid="controles-compteur"
            >
              {controlesPrets.length}/{checklist.length} prêts
            </Badge>
          ) : null}
        </div>
        <BandeauAReparer
          integre
          trip={trip}
          truck={truck}
          organizationId={user?.organization_id}
          onRepaired={loadData}
          onCount={setNbManques}
        />
        <ControlesAFaire
          checklist={checklist}
          aTraiter={controlesAtraiter}
          tripId={trip?.id}
          onAvancer={!isComplete && prochainStatut ? () => ouvrirAvancement(prochainStatut) : null}
          libelleEtape={prochainStatut ? libelle('tripStatus', prochainStatut) : ''}
          onSaisirVolume={() => setShowVolumesModal(true)}
          onAjouterPiece={ouvrirJustificatifs}
        />
      </section>

      <Tabs value={activeTab} onValueChange={selectTab} className="space-y-3" data-testid="trip-tabs">
        <TabsList className="grid h-auto w-full grid-cols-2 justify-start gap-1 sm:flex sm:flex-wrap">
          {TRIP_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="h-auto whitespace-normal py-1.5 text-center leading-tight sm:whitespace-nowrap"
              data-testid={`trip-tab-${tab.value}`}
            >
              {!canSeeMoney && tab.labelSansMontants ? tab.labelSansMontants : tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Chargement et livraison: les volumes en une ligne, le manquant
            (replié) et le contrôle de la livraison (fermé). Les contrôles à
            traiter sont plus haut, dans « À faire ». */}
        <TabsContent value="chargement" className="space-y-3">
          <section
            className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            data-testid="volumes-resume"
          >
            <h2 className="sr-only">Volumes et montants</h2>
            <dl className="grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:items-baseline sm:gap-x-6 sm:gap-y-1 [&>:last-child:nth-child(odd)]:col-span-2 sm:[&>:last-child:nth-child(odd)]:col-span-1">
              <ChiffreVolume label="Estimé" value={formatVolume(trip.fuel_quantity_estimated)} />
              <ChiffreVolume
                label="Chargé"
                aide={volume.libelle}
                value={formatVolume(volume.litres)}
              />
              {canSeeMoney ? (
                <ChiffreVolume
                  label="Prix au litre"
                  value={trip.fuel_price_unit ? `${trip.fuel_price_unit} FCFA/L` : '—'}
                />
              ) : null}
              {canSeeMoney && Number(trip.conso_espe) > 0 ? (
                <ChiffreVolume
                  label="Espèces carburant"
                  aide="Comptées dans les frais de route tant qu’aucun frais détaillé n’est saisi"
                  value={formatCurrency(trip.conso_espe)}
                />
              ) : null}
            </dl>
            {/* Un seul bouton: « Volumes » et « Montants » ouvraient deux
                formulaires qui demandaient les mêmes litres. */}
            <Button
              variant="ghost"
              size="sm"
              className="max-sm:w-full max-sm:border max-sm:border-input"
              onClick={() => setShowVolumesModal(true)}
              data-testid="edit-financial-btn"
            >
              <Edit className="h-4 w-4" aria-hidden="true" />
              Modifier
            </Button>
          </section>

          {/* Chargement et sortie (lot 056) : dépôt, N° de bon, dates, document de sortie, parking. */}
          <TripLoadingInfoCard trip={trip} onChanged={loadData} />
          {/* Voyage vers ma station (lot 032) : réception en station et chaîne dépôt → cuve. */}
          <TripStationChain tripId={trip?.id} onChanged={loadData} />
          <ShortagePanel tripId={trip?.id} repliable />
          <details className="group rounded-lg border border-border bg-card" data-testid="controle-livraison">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-foreground">
              Contrôle de la livraison
            </summary>
            <div className="border-t border-border p-3">
              <MissionIntegrityTab
                integrity={integrity}
                isRecomputing={isRecomputingIntegrity}
                onRecompute={recomputeIntegrity}
              />
            </div>
          </details>
        </TabsContent>

        {/* Argent: le fret, l'avance et le paiement (direction seulement), puis
            les frais, saisissables par la logistique. */}
        <TabsContent value="argent" className="space-y-4">
          {/* Ce que le trajet a rapporté et coûté (direction seulement). */}
          {margeVisible ? (
            <LigneArgent
              recette={economics.revenue}
              depenses={economics.total_cost}
              marge={economics.margin}
              detail={
                economics.volume_liters > 0
                  ? `${formatVolume(economics.volume_liters)} · carburant ${formatCurrency(economics.fuel_cost)} · route ${formatCurrency(economics.road_costs)}`
                  : null
              }
            />
          ) : null}

          {/* Le fret du trajet (lot 019): tarif, montant, avance et paiement reçus. */}
          <TripFreightPanel trip={trip} onChanged={loadData} />

          {/* Étape du parcours: après le chargement et la vente, ce que le trajet a coûté. */}
          <Card className="card-base" id="frais-du-trajet">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-1">
                <CardTitle>Frais du trajet</CardTitle>
                <Aide titre="Frais du trajet">Péages, pesage, douane, repas, frais supplémentaires : un frais par ligne, qu’il soit payé par le chauffeur ou par la caisse.</Aide>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ExpenseForm tripId={id} trip={trip} onSaved={handleExpenseSaved} />
              <ExpensesTable
                tripId={id}
                refreshKey={expensesVersion}
                onCancel={handleCancelExpense}
                busy={isSubmitting}
              />
            </CardContent>
          </Card>

        </TabsContent>

        {/* Pièces: les justificatifs rattachés au trajet et les observations. */}
        <TabsContent value="pieces" className="space-y-4">
          <Card className="card-base scroll-mt-20" id="justificatifs-du-trajet">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-1">
                <CardTitle>Justificatifs du trajet</CardTitle>
                <Aide titre="Justificatifs du trajet">Bon de livraison, déclaration, reçu : déposez la pièce ici, elle sera rattachée à ce trajet.</Aide>
              </div>
            </CardHeader>
            <CardContent>
              <TeleverseurEnLigne tripId={trip.id} onUploaded={loadData} />
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-1">
                <CardTitle>Observations</CardTitle>
                <Aide titre="Observations">Ce qui a été noté sur ce trajet, avec les incidents du chauffeur, les pannes et les notes de changement d’étape.</Aide>
              </div>
            </CardHeader>
            <CardContent>
              <ObservationsPanel entityType="trip" entityId={id} />
            </CardContent>
          </Card>

        </TabsContent>

        {/* Historique: les étapes franchies (frise ci-dessus), la position et
            le suivi, et le journal des changements. */}
        <TabsContent value="historique" className="space-y-4">
          <Card className="card-base">
            <CardHeader className="space-y-1">
              <CardTitle>Journal des changements</CardTitle>
            </CardHeader>
            <CardContent>
              {updates.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucun changement enregistré pour le moment.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {[...updates].reverse().map((update, index) => (
                    <li key={update.id || index} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatDateHeure(update.created_at)}
                      </span>
                      <span className="font-semibold text-foreground">
                        {libelle('tripStatus', update.status)}
                      </span>
                      {update.location_text ? (
                        <span className="text-muted-foreground">{update.location_text}</span>
                      ) : null}
                      {update.note ? (
                        <span className="text-muted-foreground">{update.note}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* GPS Tracking Map */}
          <Card className="card-base">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent-fuel" />
                  Suivi GPS en temps réel
                </CardTitle>
                <div className="flex flex-wrap items-center justify-end gap-3">
                  {/* « Il est où » se double toujours de « il en a encore pour
                      combien »: la réponse est affichée en clair, pas déduite
                      d'une longueur de trait sur la carte. */}
                  {showMap && remainingLabel && (
                    <span className="text-sm text-muted-foreground">
                      Distance restante :{' '}
                      <span className="font-mono tabular font-medium text-foreground">
                        {remainingLabel}
                      </span>
                    </span>
                  )}
                  {showMap && peutRejouer && (
                    <span className="inline-flex items-center gap-1">
                      {!rejeuOuvert && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={ouvrirRejeu}
                          disabled={rejeuChargement}
                          data-testid="rejouer-trajet"
                        >
                          {rejeuChargement ? (
                            <Loader2 className="animate-spin" aria-hidden="true" />
                          ) : (
                            <Play aria-hidden="true" />
                          )}
                          {rejeuChargement ? 'Chargement…' : 'Rejouer'}
                        </Button>
                      )}
                      <Aide
                        titre="Rejouer le trajet"
                        points={[
                          'Vert : le camion roule jusqu’à 80 km/h.',
                          'Orange : de 80 à 90 km/h.',
                          'Rouge : au-delà de 90 km/h.',
                          'Gris : le camion est à l’arrêt.',
                          'Les arrêts de plus de 10 minutes sont numérotés sur la carte.',
                        ]}
                        align="end"
                      />
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMap(!showMap)}
                  >
                    {showMap ? 'Masquer la carte' : 'Afficher la carte'}
                  </Button>
                </div>
              </div>
              {showMap && rejeuErreur && (
                <p role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-xs text-flame-ink">
                  Impossible de charger le trajet complet
                  <Button variant="outline" size="sm" onClick={ouvrirRejeu} disabled={rejeuChargement}>
                    Réessayer
                  </Button>
                </p>
              )}
            </CardHeader>
            {showMap && (
              <CardContent className="p-0 relative z-0">
                <div className="h-96 w-full rounded-lg overflow-hidden z-0">
                  {gpsData ? (
                    <MapSurface
                      layer={fondCarte}
                      center={[gpsData.latitude, gpsData.longitude]}
                      zoom={13}
                      fitTo={routePoints}
                      fitEnabled={routePoints.length > 1}
                      scrollWheelZoom={false}
                      overlay={
                        <>
                          <div className="absolute right-3 top-3 z-[400] flex max-w-[calc(100%-4.5rem)] flex-wrap items-start justify-end gap-2">
                            <ZonesFiltre
                              zones={mapZones}
                              masquees={famillesMasquees}
                              onChange={setFamillesMasquees}
                            />
                            <SelecteurFond valeur={fondCarte} onChange={choisirFond} />
                          </div>
                          {rejeuActif && (
                            <LecteurTrajet
                              points={rejeuPoints}
                              onPosition={setRejeuIndex}
                              onFermer={fermerRejeu}
                            />
                          )}
                        </>
                      }
                    >
                      <MoletteSurFocus />
                      <ZoneLayer zones={zonesVisibles} />
                      <RouteLayer
                        geometry={routePoints}
                        position={currentPosition}
                        originLabel="Départ"
                        destinationLabel="Arrivée"
                      />
                      {rejeuActif && rejeuAnalyse && (
                        <CoucheRejeu
                          segments={rejeuAnalyse.segments}
                          arrets={rejeuAnalyse.arrets}
                          index={rejeuIndex}
                        />
                      )}
                      {rejeuActif && pointRejeu && (
                        <Marker
                          position={[Number(pointRejeu.latitude), Number(pointRejeu.longitude)]}
                          icon={iconeCamionRejeu}
                          zIndexOffset={1000}
                        >
                          <Popup>
                            <div className="text-sm">
                              <p className="font-bold">Position à cette heure</p>
                              <p>Vitesse : {Math.round(Number(pointRejeu.speed_kmh) || 0)} km/h</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDateHeure(pointRejeu.timestamp)}
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      {!rejeuActif && (
                        <Marker
                          position={[gpsData.latitude, gpsData.longitude]}
                          icon={iconeCamion}
                        >
                          <Popup>
                            <div className="text-sm">
                              <p className="font-bold">Position actuelle</p>
                              <p>Latitude : {gpsData.latitude.toFixed(6)}</p>
                              <p>Longitude : {gpsData.longitude.toFixed(6)}</p>
                              <p>Vitesse : {gpsData.speed_kmh} km/h</p>
                              {gpsData.fuel_level_percent !== null && (
                                <p>Carburant : {gpsData.fuel_level_percent} %</p>
                            )}
                            {remainingLabel && <p>Restant : {remainingLabel}</p>}
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateHeure(gpsData.timestamp)}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                      )}
                      {/* La trace est le chemin réellement pris; le tracé
                          d'itinéraire, celui qui était prévu. Les deux se lisent
                          ensemble, d'où deux styles distincts. */}
                      {!rejeuActif && trailPositions.length > 1 && (
                        <Polyline
                          positions={trailPositions}
                          color="hsl(var(--flame))"
                          weight={4}
                          opacity={0.8}
                        />
                      )}
                      {/* Roulé à plus de 5 km de l'itinéraire prévu : même seuil
                          que l'alerte « Camion hors itinéraire ». */}
                      {!rejeuActif &&
                        horsItineraire.map((portion, rang) => (
                          <Polyline
                            key={`hors-itineraire-${rang}`}
                            positions={portion}
                            pathOptions={{ color: 'hsl(var(--destructive))', weight: 6, opacity: 0.9, dashArray: '8 6' }}
                          />
                        ))}
                    </MapSurface>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-muted">
                      <div className="text-center">
                        <MapPin className="w-12 h-12 mx-auto text-muted-foreground/70 mb-2" />
                        <p className="text-muted-foreground">
                          {trackingConnected ? 'En attente des données GPS...' : 'Aucune donnée GPS disponible'}
                        </p>
                        {trackingConnected && (
                          <div className="flex items-center justify-center gap-2 mt-2">
                            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                            <span className="text-sm text-success">Connecté</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

        </TabsContent>
      </Tabs>

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mettre à jour le statut</DialogTitle>
            <DialogDescription>
              Sélectionnez le nouveau statut et ajoutez des informations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-muted/30 rounded-md p-3 border border-border/60">
              <p className="readout-label">
                Statut actuel
              </p>
              <div className="mt-2">
                <Badge
                  className={cn(
                    'status-badge',
                    getStatusBadgeClass(trip.status),
                  )}
                >
                  {libelle('tripStatus', trip.status)}
                </Badge>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1">
                <Label className="label-base">Nouveau statut</Label>
                {nextStatuses.length > 0 ? (
                  <Aide titre="Nouveau statut">
                    Seules les étapes atteignables depuis l’étape actuelle sont
                    proposées.{managesDepot ? ' Après l’entrée validée, le passage au dépôt est facultatif.' : ''}
                  </Aide>
                ) : null}
              </div>
              <Select value={newStatus} onValueChange={handleNewStatusChange}>
                <SelectTrigger data-testid="new-status-select">
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent>
                  {nextStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {libelle('tripStatus', status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-base">Localisation</Label>
              <Input
                placeholder="Ex: Kidira, frontière"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                className="input-base"
                data-testid="location-input"
              />
            </div>

            {/* Le contrat métier disait « 2 blocages » derrière une modale à
                consulter. Ce qui manque est maintenant dans le bandeau de la
                page, avec le bouton qui le corrige — pas dans une deuxième
                modale ouverte par-dessus celle-ci. */}
            {isCustomsStatus(newStatus) && (
              <div>
                <Label className="label-base">Poste douanier</Label>
                <Input
                  placeholder="Ex: Poste frontière Kidira"
                  value={newCustomsCheckpoint}
                  onChange={(e) => setNewCustomsCheckpoint(e.target.value)}
                  className="input-base"
                  data-testid="customs-input"
                />
              </div>
            )}

            <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="label-base mb-0">Note (optionnel)</Label>
                  <VoiceInputButton 
                    onTranscript={(text) => setNewNote(prev => prev ? prev + ' ' + text : text)} 
                  />
                </div>
                <Textarea
                  placeholder="Ajouter une note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="input-base min-h-[80px]"
                  data-testid="note-input"
                />
              </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowStatusModal(false)}
              className="w-full sm:w-auto mt-2 sm:mt-0"
            >
              Annuler
            </Button>
            <div className="w-full sm:w-[280px]">
              <SwipeToConfirm
                onConfirm={handleStatusUpdate}
                isLoading={isSubmitting}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Volumes et montants — une seule modale. Les deux qu'elle remplace
          demandaient chacune la consommation réelle et le carburant manquant,
          déjà saisis à la création du trajet: trois fois la même information,
          pour un litrage qui ne se mesure qu'une fois. */}
      <Dialog open={showVolumesModal} onOpenChange={setShowVolumesModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Volumes et montants</DialogTitle>
            <DialogDescription>
              Ce que le camion a réellement porté, et ce que le trajet coûte.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Volume chargé (L)</Label>
              <Input
                type="number"
                placeholder="Ex: 40000"
                value={saisieVolumes.fuel_quantity_real}
                onChange={(e) =>
                  setSaisieVolumes((prev) => ({
                    ...prev,
                    fuel_quantity_real: e.target.value,
                  }))
                }
                className="input-base"
              />
              <p className="mt-1 text-xs text-muted-foreground">Mesuré au chargement</p>
            </div>

            <div>
              <Label className="label-base">Carburant manquant (L)</Label>
              <Input
                type="number"
                placeholder="Ex: 120"
                value={saisieVolumes.fuel_missing_quantity}
                onChange={(e) =>
                  setSaisieVolumes((prev) => ({
                    ...prev,
                    fuel_missing_quantity: e.target.value,
                  }))
                }
                className="input-base"
              />
            </div>

            <div>
              <Label className="label-base">Prix unitaire (FCFA/L)</Label>
              <Input
                type="number"
                placeholder={
                  prixBareme != null ? String(prixBareme) : 'Prix au litre'
                }
                value={saisieVolumes.fuel_price_unit}
                onChange={(e) => {
                  setPrixTouche(true);
                  setSaisieVolumes((prev) => ({
                    ...prev,
                    fuel_price_unit: e.target.value,
                  }));
                }}
                className="input-base"
              />
              {/* D'où vient le chiffre. Sans cette ligne, un prix qui apparaît
                  tout seul dans un champ est un prix qu'on ne croit pas. */}
              <p className="mt-1 text-xs text-muted-foreground">
                {chargementBareme
                  ? 'Lecture du barème…'
                  : nomBareme
                    ? `Proposé par le barème « ${nomBareme} » — modifiable.`
                    : aucunBareme
                      ? 'Aucun barème ne couvre ce convoi : saisissez le prix convenu.'
                      : 'Modifiable : un prix négocié l’emporte sur le barème.'}
              </p>
            </div>

            {/* Repliés parce qu'ils se renseignent à la création du trajet et
                se corrigent rarement — jamais supprimés: le dépliant s'ouvre
                de lui-même dès que l'un d'eux porte une valeur. */}
            <ChampsAvances
              titre="Planning et espèces"
              consequence="Retour prévu, volume estimé au départ et espèces remises pour le carburant."
              valeurs={[
                saisieVolumes.return_date,
                saisieVolumes.fuel_quantity_estimated,
                saisieVolumes.conso_espe,
              ]}
            >
              <div>
                <Label className="label-base">Retour prévu</Label>
                <Input
                  type="date"
                  value={saisieVolumes.return_date}
                  onChange={(e) =>
                    setSaisieVolumes((prev) => ({
                      ...prev,
                      return_date: e.target.value,
                    }))
                  }
                  className="input-base"
                />
              </div>

              <div>
                <Label className="label-base">Quantité estimée (L)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 40000"
                  value={saisieVolumes.fuel_quantity_estimated}
                  onChange={(e) =>
                    setSaisieVolumes((prev) => ({
                      ...prev,
                      fuel_quantity_estimated: e.target.value,
                    }))
                  }
                  className="input-base"
                />
              </div>

              <div>
                <Label className="label-base">Espèces remises pour le carburant (FCFA)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 25000"
                  value={saisieVolumes.conso_espe}
                  onChange={(e) =>
                    setSaisieVolumes((prev) => ({
                      ...prev,
                      conso_espe: e.target.value,
                    }))
                  }
                  className="input-base"
                />
                <Aide titre="Espèces remises pour le carburant">
                  Compté dans les frais de route tant qu’aucun frais détaillé n’est saisi
                </Aide>
              </div>
            </ChampsAvances>

            <div className="col-span-1 md:col-span-2 mt-4">
              <Aide titre="Frais du trajet" libelle="Où saisir les frais ?">
                Les frais du trajet (péages, pesage, frais supplémentaires…) se saisissent dans le cadre « Frais du trajet », sous cette fenêtre.
              </Aide>
            </div>

            {/* Le montant enregistré, et le seul. Le champ « si différent »
                qui suivait permettait de contredire ce calcul deux lignes plus
                bas: l'écart qui en sortait n'était explicable par personne. */}
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="readout-label mb-1 flex items-center gap-1">
                Montant du trajet
                <Aide titre="Montant du trajet">
                  Consommation × prix unitaire, plus les espèces et les frais de
                  route.
                </Aide>
              </p>
              <p className="kpi-value text-flame">
                {formatCurrency(calculateTotal())}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVolumesModal(false)}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={enregistrerVolumesEtMontants}
              disabled={isSubmitting}
              data-testid="confirm-trip-details-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LienSuiviDialog open={showLienSuivi} onOpenChange={setShowLienSuivi} tripId={trip?.id} />

      {/* Delete confirm */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce trajet ?</AlertDialogTitle>
            <AlertDialogDescription>
              La suppression est définitive : l’historique des étapes du trajet sera
              aussi supprimé. Si le trajet est actif, le camion repassera en Libre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrip}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TripDetailPage;

/** Un fait de la ligne d'identité: icône, puis la valeur (libellé pour les lecteurs d'écran et en infobulle). */
function IdentityFact({ icon: Icon, label, vide = false, texteVide = '', children }) {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5" title={label}>
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">{label} : </span>
      {vide ? (
        <span className="text-muted-foreground">{texteVide}</span>
      ) : (
        <span className="min-w-0 truncate">{children}</span>
      )}
    </span>
  );
}

/** Un chiffre de la ligne des volumes. Chiffres tabulaires pour que les nombres s'alignent. */
function ChiffreVolume({ label, value, aide }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-1.5">
      <dt className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {aide ? <Aide titre={label}>{aide}</Aide> : null}
      </dt>
      <dd className="whitespace-nowrap font-mono text-sm font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

/* Réconciliation triangulaire — ce que le serveur calcule déjà et que personne
   ne voyait: GPS parcouru, consommation théorique contre consommation mesurée
   à la jauge, ravitaillements lus sur les tickets, et l'écart chargé/livré.
   Le verdict vient du moteur unique (`core/volume_variance`), donc l'écran ne
   rejuge rien: il cite. */

const BLOCKED_REASON_LABELS = {
  CONSUMPTION_VARIANCE_GT_3_PERCENT:
    'La consommation mesurée à la jauge s’écarte de la consommation théorique au-delà de la tolérance.',
  DELIVERY_VARIANCE_OUT_OF_TOLERANCE:
    'L’écart entre volume chargé et volume livré dépasse les deux bornes de tolérance.',
};

const MISSING_SOURCE_LABELS = {
  gps_history: 'pas de trace GPS sur ce trajet',
  gps_distance_or_vehicle_yield:
    'pas de distance GPS ni de rendement véhicule renseigné sur le camion',
  tank_sensor_initial_final: 'pas de relevé de jauge au départ et à l’arrivée',
  loaded_or_delivered_volume: 'volume chargé ou livré manquant',
  loaded_volume: 'volume chargé non renseigné',
  delivered_volume: 'volume livré non renseigné',
  delivery_document: 'aucun bon de livraison rattaché',
};

const VERDICT_TONE = { ok: 'positive', watch: 'attention', breach: 'critical' };
const VERDICT_LABEL = {
  ok: 'Dans la tolérance',
  watch: 'À surveiller',
  breach: 'Hors tolérance',
};

/** Litres, ou un tiret quand la mesure n'existe pas. Zéro et « inconnu » ne
    sont pas la même information sur un contrôle de volume. */
const liters = (value) =>
  value === null || value === undefined ? '—' : formatVolume(value);

function MissionIntegrityTab({ integrity, isRecomputing, onRecompute }) {
  const recomputeButton = (
    <Button variant="outline" size="sm" onClick={onRecompute} disabled={isRecomputing}>
      {isRecomputing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      Recalculer
    </Button>
  );

  if (!integrity) {
    return (
      <Card className="card-base">
        <CardContent className="p-0">
          <EmptyState
            variant="empty"
            icon={Fuel}
            title="Le contrôle de la livraison n’a pas encore été fait"
            description="Il compare le GPS, la jauge et les ravitaillements. Lancez-le quand vous voulez ; il se refait aussi après chaque livraison ou correction."
            action={recomputeButton}
            className="border-0"
          />
        </CardContent>
      </Card>
    );
  }

  const inputs = integrity.inputs_json || {};
  const triangular = inputs.triangular_reconciliation || {};
  const verdict = triangular.delivery_verdict || inputs.delivery_variance_verdict || null;
  const blockedReasons = triangular.blocked_reasons || [];
  const missingSources = inputs.missing_sources || [];
  const idleHours = triangular.idle_seconds
    ? Math.round((triangular.idle_seconds / 3600) * 10) / 10
    : null;

  const rows = [
    {
      label: 'Consommation camion',
      expected: liters(triangular.theoretical_consumption_liters),
      actual: liters(triangular.real_consumption_liters),
      delta: liters(triangular.consumption_delta_liters),
      hint: triangular.consumption_delta_percent != null
        ? `${triangular.consumption_delta_percent} % · tolérance ${triangular.consumption_tolerance_percent} %`
        : 'Théorique = distance GPS × rendement + ralenti',
    },
    {
      label: 'Volume transporté',
      expected: liters(triangular.loaded_volume_liters),
      actual: liters(triangular.delivered_volume_liters),
      delta: liters(triangular.delivery_delta_liters),
      hint: verdict
        ? `${verdict.delta_percent} % · tolérance ${verdict.threshold_liters} L et ${verdict.threshold_percent} %`
        : 'Chargé contre livré',
    },
    {
      label: 'Jauge de cuve',
      expected: liters(triangular.gauge_initial_liters),
      actual: liters(triangular.gauge_final_liters),
      delta: '—',
      hint: triangular.tank_capacity_liters
        ? `${triangular.gauge_initial_percent ?? '—'} % → ${triangular.gauge_final_percent ?? '—'} % d’une cuve de ${formatVolume(triangular.tank_capacity_liters)}`
        : 'Capacité de cuve non renseignée sur le camion',
    },
    {
      label: 'Ravitaillements (tickets)',
      expected: '—',
      actual: liters(triangular.refuel_volume_liters),
      delta: '—',
      hint: 'Volume lu sur les tickets de ravitaillement rattachés au trajet',
    },
    {
      label: 'Temps de ralenti',
      expected: '—',
      actual: idleHours === null ? '—' : `${idleHours} h`,
      delta: '—',
      hint: 'Moteur tournant à l’arrêt, compté dans la consommation théorique',
    },
  ];

  return (
    <div className="space-y-4">
      <StatsCompactes
        stats={[
          {
            label: 'Note de fiabilité',
            valeur: `${Number(integrity.score ?? 0)} / 100`,
            ton: Number(integrity.score ?? 0) >= 80 ? 'positif' : 'critique',
            aide: libelle('statutIntegrite', integrity.status),
          },
          {
            label: 'Écart de volume',
            valeur: liters(integrity.volume_variance_liters),
            ton: verdict
              ? { critical: 'critique', attention: 'attention', positive: 'positif' }[
                  VERDICT_TONE[verdict.severity]
                ] || 'neutre'
              : 'neutre',
            aide: verdict
              ? `${VERDICT_LABEL[verdict.severity] || 'À vérifier'} · ${verdict.delta_percent} %`
              : 'Verdict indisponible',
          },
          {
            label: 'Distance GPS',
            valeur: integrity.gps_distance_km == null ? '—' : `${integrity.gps_distance_km} km`,
            aide: `${inputs.gps?.point_count ?? 0} points relevés`,
          },
          {
            label: 'Perte estimée',
            valeur: formatCurrency(integrity.estimated_loss_value || 0),
            ton: Number(integrity.estimated_loss_value || 0) > 0 ? 'critique' : 'positif',
            aide: integrity.fuel_unit_price
              ? `${integrity.fuel_unit_price} FCFA/L`
              : 'Prix unitaire non renseigné',
          },
        ]}
      />

      {blockedReasons.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Livraison à contrôler — vous pouvez poursuivre</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              {blockedReasons.map((reason) => (
                <li key={reason}>{BLOCKED_REASON_LABELS[reason] || 'Un écart reste à contrôler.'}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card className="card-base">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Comparaison des quantités</CardTitle>
          {recomputeButton}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grandeur</TableHead>
                <TableHead>Attendu</TableHead>
                <TableHead>Constaté</TableHead>
                <TableHead>Écart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>
                    <p className="font-medium text-foreground">{row.label}</p>
                    <p className="text-xs text-muted-foreground">{row.hint}</p>
                  </TableCell>
                  <TableCell className="font-mono tabular-nums">{row.expected}</TableCell>
                  <TableCell className="font-mono tabular-nums">{row.actual}</TableCell>
                  <TableCell className="font-mono tabular-nums">{row.delta}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {missingSources.length > 0 && (
        <EmptyState
          variant="filtered"
          title="Contrôle incomplet"
          description={`Mesures manquantes : ${missingSources
            .map((source) => MISSING_SOURCE_LABELS[source] || 'une mesure manque')
            .join(', ')}.`}
          aide="Les lignes concernées restent vides plutôt que de porter un verdict sans mesure."
        />
      )}
    </div>
  );
}

function ControlesAFaire({
  checklist = [],
  aTraiter = [],
  tripId,
  onAvancer = null,
  libelleEtape = '',
  onSaisirVolume = null,
  onAjouterPiece = null,
}) {
  const navigate = useNavigate();
  const { hasModule } = useAuth();
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const labelFor = (item) => {
    if (item.key === 'order') return 'Commande liée';
    if (item.key === 'workflow') return 'Prochaine action';
    if (item.key === 'documents') return item.ready ? 'Pièce requise OK' : 'Pièce requise';
    if (item.key === 'volume') return 'Volume';
    if (item.key === 'reconciliation') return item.status === 'warning' ? 'Volumes incomplets' : item.ready ? 'Volume validé' : 'Écart volume à valider';
    if (item.key === 'stock') return 'Stock';
    if (item.key === 'invoice') return 'Facturation';
    if (item.key === 'gps') return item.ready ? 'Dernier point GPS fiable' : 'GPS à vérifier';
    return texteSur(item.label, 'Contrôle');
  };
  // Le poids suit l'exception: seul ce qui reste à traiter prend une ligne,
  // avec sa raison et son bouton. Ce qui est réussi n'est plus qu'un compteur,
  // dans l'en-tête de la zone « À faire ».
  const exceptions = aTraiter;
  const commandeLiee = Boolean(checklist.find((item) => item.key === 'order')?.ready);
  const peutOuvrir = (module) => typeof hasModule !== 'function' || hasModule(module);

  // Un bouton par ligne, et seulement un geste qui existe déjà ailleurs: on
  // renvoie vers l'écran qui sait le faire (ou on ouvre la fenêtre de cette
  // page). Rien n'est écrit d'ici — surtout pas le lien commande/trajet, qui
  // passe par la Répartition pour que le stock suive.
  const actionPour = (item) => {
    switch (item.key) {
      case 'order':
        return tripId && peutOuvrir('dispatch_allocations')
          ? { texte: 'Rattacher à une commande', icone: Link2, onClick: () => navigate(`/dispatch?trajet=${encodeURIComponent(tripId)}`) }
          : null;
      case 'workflow':
        return onAvancer
          ? { texte: libelleEtape || 'Faire avancer', icone: ArrowRight, ecrit: true, onClick: onAvancer }
          : null;
      case 'documents':
        return onAjouterPiece ? { texte: 'Ajouter une pièce', icone: Paperclip, ecrit: true, onClick: onAjouterPiece } : null;
      case 'volume':
        return onSaisirVolume ? { texte: 'Saisir le volume', icone: Scale, ecrit: true, onClick: onSaisirVolume } : null;
      case 'reconciliation':
        if (item.status === 'warning') {
          // Volumes incomplets: il n'y a pas d'écart à traiter, il manque des litres.
          return onSaisirVolume ? { texte: 'Saisir le volume', icone: Scale, ecrit: true, onClick: onSaisirVolume } : null;
        }
        return tripId && peutOuvrir('stock_movements')
          ? { texte: 'Traiter cet écart', icone: Scale, onClick: () => navigate(`/stock?tab=variances&trip_id=${encodeURIComponent(tripId)}`) }
          : null;
      case 'stock':
        return peutOuvrir('stock_movements')
          ? { texte: 'Voir le stock', icone: Package, onClick: () => navigate('/stock') }
          : null;
      case 'invoice':
        // Sans commande, la facture attend le rattachement. Avec une commande
        // mais sans livraison, le texte suffit: rien à faire d'ici.
        return !commandeLiee && tripId && peutOuvrir('dispatch_allocations')
          ? { texte: 'Rattacher à une commande', icone: Link2, onClick: () => navigate(`/dispatch?trajet=${encodeURIComponent(tripId)}`) }
          : null;
      case 'gps':
        return peutOuvrir('gps_tracking')
          ? { texte: 'Voir le suivi', icone: Navigation, onClick: () => navigate('/tracking') }
          : null;
      default:
        return null;
    }
  };

  if (exceptions.length === 0) return null;

  return (
    <ul className="divide-y divide-border border-t border-border" data-testid="controles-a-traiter">
      {exceptions.map((item) => {
        const isWatch = ['watch', 'warning'].includes(item.status);
        const action = actionPour(item);
        return (
          <li key={item.key} className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-card px-4 py-2.5">
            <AlertTriangle
              className={`h-4 w-4 shrink-0 self-start ${isWatch ? 'text-flame-ink' : 'text-destructive'} mt-0.5`}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1 basis-56">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-foreground">
                {labelFor(item)}
                <Badge variant={isWatch ? 'outline' : 'destructive'} className="shrink-0 font-normal">
                  {item.status === 'warning' ? 'À renseigner' : isWatch ? 'À surveiller' : 'Bloqué'}
                </Badge>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{texteSur(item.reason, '')}</p>
              {item.next_action && <p className="mt-0.5 text-sm text-foreground">Action : {texteSur(item.next_action, '')}</p>}
            </div>
            {action ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full max-w-full shrink-0 sm:w-auto"
                disabled={action.ecrit && !enLigne}
                onClick={action.onClick}
                data-testid={`controle-action-${item.key}`}
              >
                <action.icone className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 truncate">{action.texte}</span>
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
